import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const LanguageContext = createContext(null);
const STORAGE = "peptideseries.lang";

const DICT = {
  en: {
    // header
    "nav.catalog": "Catalog",
    "nav.antiaging": "Anti-aging",
    "nav.weightloss": "Weight Loss",
    "nav.fitness": "Fitness",
    "nav.quality": "Quality",
    "nav.cart": "Cart",
    "nav.signin": "Sign in",
    "nav.register": "Doctor sign-up",
    "nav.dashboard": "Dashboard",
    "nav.verification": "Verification (ID + License)",
    "nav.signout": "Sign out",
    "nav.account": "Account",
    // hero / home
    "home.cta.doctor": "Doctor sign-up",
    "home.cta.browse": "Browse catalog",
    "home.access.title": "Reserved for licensed physicians.",
    "home.access.overline": "Access protocol",
    "home.access.subtitle":
      "PeptideSeries is closed to the public. Doctors verify their identity and Exequatur or Colegiatura through the platform administrator before any pricing or ordering is unlocked.",
    "home.doctor.flow": "How verification works",
    "home.doctor.s1": "Sign up with email or Google.",
    "home.doctor.s2": "Upload a government-issued ID photo.",
    "home.doctor.s3": "Upload your Exequatur or Colegiatura.",
    "home.doctor.s4": "Admin reviews both (≤48 h). Pricing & ordering unlock.",
    "home.doctor.start": "Start doctor sign-up",
    "home.cat.title": "Peptides protocols, by therapeutic use.",
    "home.cat.overline": "Categories",
    "home.qa.overline": "Quality assurance",
    "home.qa.title": "Every vial, every pen — reconciled to a batch certificate.",
    "home.qa.body":
      "We publish the chain of custody from synthesis to dispensing. Independent third-party HPLC and mass-spec assays verify identity and purity before any order leaves the facility.",
    // catalog
    "catalog.title": "Peptide protocols.",
    "catalog.overline": "Catalog",
    "catalog.subtitle":
      "Browse openly. Pricing and ordering are unlocked once your ID and medical license are approved by the administrator.",
    "catalog.filter.all": "All",
    "catalog.empty": "No products in this category yet.",
    "catalog.details": "Details →",
    "catalog.priceLocked": "Sign in to view",
    "catalog.priceLockedVerified": "Verification required",
    // product detail
    "pd.presentation": "Presentation",
    "pd.dose": "Dose",
    "pd.stock": "Stock",
    "pd.stock.in": "Available",
    "pd.stock.out": "Out of stock",
    "pd.unitPrice": "Unit price",
    "pd.unitPrice.locked": "Hidden",
    "pd.access.signin": "Access restricted. Sign in as a verified doctor to view pricing and order.",
    "pd.access.verify": "Verification required.",
    "pd.access.id": "Identity document",
    "pd.access.license": "Medical license (Exequatur / Colegiatura)",
    "pd.access.note":
      "Both documents must be approved by admin before pricing and ordering are unlocked.",
    "pd.manage": "Manage verification",
    "pd.qa": "HPLC ≥99% · Per-batch certificate of analysis available on request.",
    "pd.add": "Add to cart",
    // login
    "login.title": "Welcome back, doctor.",
    "login.subtitle": "Use your email + password, or continue with Google.",
    "login.google": "Continue with Google",
    "login.or": "or",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.submitting": "Signing in…",
    "login.newDoctor": "New here?",
    "login.registerLink": "Create a doctor account",
    "login.signedAccess": "Verified clinician access",
    "login.aside":
      "Doctors only. ID and medical license are reviewed before pricing and orders are enabled.",
    // register doctor
    "reg.title": "Create your doctor account.",
    "reg.overline": "Doctor sign-up",
    "reg.subtitle":
      "After registration you will be asked to upload your ID and your Exequatur or Colegiatura. Pricing and orders unlock once admin approves both (typically within 48 hours).",
    "reg.name": "Full name",
    "reg.email": "Email",
    "reg.password": "Password (min 8 characters)",
    "reg.license": "License number (optional)",
    "reg.specialty": "Medical specialty",
    "reg.whatsapp": "WhatsApp number",
    "reg.whatsapp.hint": "Include country code, e.g. +1 809 555 1234",
    "reg.submit": "Create doctor account",
    "reg.submitting": "Creating account…",
    "reg.has": "Already registered?",
    "reg.has.link": "Sign in",
    // cart
    "cart.title": "Review your order",
    "cart.empty": "Your cart is empty.",
    "cart.summary": "Summary",
    "cart.subtotal": "Subtotal",
    "cart.shipping": "Shipping",
    "cart.shippingNote": "Calculated at review",
    "cart.total": "Total",
    "cart.address": "Shipping address",
    "cart.notes": "Notes (optional)",
    "cart.signin": "Sign in to place order",
    "cart.place": "Place order",
    "cart.placing": "Placing order…",
    "cart.gate":
      "Both your ID and your medical license must be approved before placing an order.",
    "cart.gate.btn": "Manage verification",
    "cart.priceLocked": "Pricing hidden until verified",
    // verification page
    "ver.title.verified": "Verified — you can now place orders.",
    "ver.title.required":
      "Two documents required: ID photo and medical license (Exequatur or Colegiatura).",
    "ver.body":
      "Both documents must be approved by an administrator before any order is permitted. Files are encrypted at rest and visible only to you and the reviewing admin.",
    "ver.id": "Identity document",
    "ver.license": "Medical license (Exequatur / Colegiatura)",
    "ver.id.note":
      "Government-issued photo ID. Accepted: PDF, JPG, PNG, WEBP. Max 10MB.",
    "ver.license.note":
      "Exequatur, Colegiatura or equivalent national medical license. Accepted: PDF, JPG, PNG, WEBP. Max 10MB.",
    "ver.choose": "Choose file",
    "ver.uploading": "Uploading…",
    "ver.history": "History",
    "ver.status.approved": "Approved",
    "ver.status.pending": "Under review — typically within 48 hours",
    "ver.status.rejected": "Rejected — please upload a new file",
    "ver.status.none": "Not yet uploaded",
    "ver.overline": "Account verification",
  },

  es: {
    // header
    "nav.catalog": "Catálogo",
    "nav.antiaging": "Antienvejecimiento",
    "nav.weightloss": "Pérdida de peso",
    "nav.fitness": "Fitness",
    "nav.quality": "Calidad",
    "nav.cart": "Carrito",
    "nav.signin": "Iniciar sesión",
    "nav.register": "Registro de médico",
    "nav.dashboard": "Panel",
    "nav.verification": "Verificación (ID + Licencia)",
    "nav.signout": "Cerrar sesión",
    "nav.account": "Cuenta",
    // hero / home
    "home.cta.doctor": "Registro de médico",
    "home.cta.browse": "Ver catálogo",
    "home.access.title": "Reservado a médicos colegiados.",
    "home.access.overline": "Protocolo de acceso",
    "home.access.subtitle":
      "PeptideSeries no es público. Los médicos verifican su identidad y su Exequatur o Colegiatura con el administrador antes de que se desbloqueen los precios y las compras.",
    "home.doctor.flow": "Cómo funciona la verificación",
    "home.doctor.s1": "Regístrate con correo o Google.",
    "home.doctor.s2": "Sube una foto de tu documento de identidad oficial.",
    "home.doctor.s3": "Sube tu Exequatur o Colegiatura.",
    "home.doctor.s4": "El administrador revisa ambos (≤48 h). Se desbloquean precios y compras.",
    "home.doctor.start": "Comenzar registro de médico",
    "home.cat.title": "Protocolos de péptidos, por uso terapéutico.",
    "home.cat.overline": "Categorías",
    "home.qa.overline": "Garantía de calidad",
    "home.qa.title":
      "Cada vial, cada pluma — conciliados con un certificado de lote.",
    "home.qa.body":
      "Publicamos la cadena de custodia desde la síntesis hasta la dispensación. Ensayos HPLC y de espectrometría de masas independientes verifican identidad y pureza antes de que cualquier pedido salga de la planta.",
    // catalog
    "catalog.title": "Protocolos peptídicos.",
    "catalog.overline": "Catálogo",
    "catalog.subtitle":
      "Explora libremente. Los precios y la compra se desbloquean cuando el administrador apruebe tu identidad y tu licencia médica.",
    "catalog.filter.all": "Todos",
    "catalog.empty": "Aún no hay productos en esta categoría.",
    "catalog.details": "Detalles →",
    "catalog.priceLocked": "Inicia sesión para ver el precio",
    "catalog.priceLockedVerified": "Verificación requerida",
    // product detail
    "pd.presentation": "Presentación",
    "pd.dose": "Dosis",
    "pd.stock": "Stock",
    "pd.stock.in": "Disponible",
    "pd.stock.out": "Agotado",
    "pd.unitPrice": "Precio unitario",
    "pd.unitPrice.locked": "Oculto",
    "pd.access.signin":
      "Acceso restringido. Inicia sesión como médico verificado para ver precios y comprar.",
    "pd.access.verify": "Verificación requerida.",
    "pd.access.id": "Documento de identidad",
    "pd.access.license": "Licencia médica (Exequatur / Colegiatura)",
    "pd.access.note":
      "Ambos documentos deben ser aprobados por el administrador antes de que se desbloqueen los precios y la compra.",
    "pd.manage": "Gestionar verificación",
    "pd.qa":
      "HPLC ≥99% · Certificado de análisis por lote disponible bajo petición.",
    "pd.add": "Añadir al carrito",
    // login
    "login.title": "Bienvenido, doctor.",
    "login.subtitle":
      "Usa tu correo + contraseña, o continúa con Google.",
    "login.google": "Continuar con Google",
    "login.or": "o",
    "login.email": "Correo",
    "login.password": "Contraseña",
    "login.submit": "Iniciar sesión",
    "login.submitting": "Iniciando sesión…",
    "login.newDoctor": "¿Eres nuevo?",
    "login.registerLink": "Crea una cuenta de médico",
    "login.signedAccess": "Acceso clínico verificado",
    "login.aside":
      "Solo médicos. La identidad y la licencia médica se revisan antes de habilitar precios y compras.",
    // register doctor
    "reg.title": "Crea tu cuenta de médico.",
    "reg.overline": "Registro de médico",
    "reg.subtitle":
      "Tras el registro deberás subir tu documento de identidad y tu Exequatur o Colegiatura. Los precios y las compras se desbloquean cuando el administrador apruebe ambos (normalmente en menos de 48 h).",
    "reg.name": "Nombre completo",
    "reg.email": "Correo",
    "reg.password": "Contraseña (mín. 8 caracteres)",
    "reg.license": "Número de colegiado (opcional)",
    "reg.specialty": "Especialidad médica",
    "reg.whatsapp": "Número de WhatsApp",
    "reg.whatsapp.hint": "Incluye código de país, ej. +1 809 555 1234",
    "reg.submit": "Crear cuenta de médico",
    "reg.submitting": "Creando cuenta…",
    "reg.has": "¿Ya estás registrado?",
    "reg.has.link": "Iniciar sesión",
    // cart
    "cart.title": "Revisa tu pedido",
    "cart.empty": "Tu carrito está vacío.",
    "cart.summary": "Resumen",
    "cart.subtotal": "Subtotal",
    "cart.shipping": "Envío",
    "cart.shippingNote": "Calculado al revisar",
    "cart.total": "Total",
    "cart.address": "Dirección de envío",
    "cart.notes": "Notas (opcional)",
    "cart.signin": "Inicia sesión para hacer el pedido",
    "cart.place": "Realizar pedido",
    "cart.placing": "Procesando pedido…",
    "cart.gate":
      "Tu identidad y tu licencia médica deben estar aprobadas antes de poder comprar.",
    "cart.gate.btn": "Gestionar verificación",
    "cart.priceLocked": "Precios ocultos hasta la verificación",
    // verification page
    "ver.title.verified": "Verificado — ya puedes comprar.",
    "ver.title.required":
      "Se requieren dos documentos: identidad y licencia médica (Exequatur o Colegiatura).",
    "ver.body":
      "Ambos documentos deben ser aprobados por un administrador antes de permitir compras. Los archivos están cifrados en reposo y solo son visibles para ti y el administrador revisor.",
    "ver.id": "Documento de identidad",
    "ver.license": "Licencia médica (Exequatur / Colegiatura)",
    "ver.id.note": "Documento oficial con foto. Aceptado: PDF, JPG, PNG, WEBP. Máx. 10MB.",
    "ver.license.note":
      "Exequatur, Colegiatura o licencia médica nacional equivalente. Aceptado: PDF, JPG, PNG, WEBP. Máx. 10MB.",
    "ver.choose": "Elegir archivo",
    "ver.uploading": "Subiendo…",
    "ver.history": "Historial",
    "ver.status.approved": "Aprobado",
    "ver.status.pending": "En revisión — normalmente en menos de 48 h",
    "ver.status.rejected": "Rechazado — sube un archivo nuevo",
    "ver.status.none": "Aún no subido",
    "ver.overline": "Verificación de cuenta",
  },
};

function detectBrowserLang() {
  // Public site is English-only. Dashboard honours localStorage toggle.
  return "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE);
      if (stored === "en" || stored === "es") return stored;
    } catch (err) {
      console.warn("Failed to read language from localStorage:", err?.message || err);
    }
    return detectBrowserLang();
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(STORAGE, lang);
    } catch (err) {
      console.warn("Failed to persist language to localStorage:", err?.message || err);
    }
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "es" ? "es" : "en"), []);

  const t = useCallback(
    (key, fallback) => {
      // Public site is English-only; only /dashboard/* honours the toggle.
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "/";
      const effectiveLang = pathname.startsWith("/dashboard") ? lang : "en";
      const dict = DICT[effectiveLang] || DICT.en;
      return dict[key] ?? DICT.en[key] ?? fallback ?? key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
}

/** Doctor must have BOTH ID and license approved. Admins always see prices. */
export function canSeePrices(user) {
  if (!user || user === false) return false;
  if (user.role === "admin") return true;
  return user.role === "doctor"
    && user.id_document_status === "approved"
    && user.license_status === "approved";
}
